import React from 'react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell,
    Brush,
} from 'recharts';
import { ChartType, transformDataForChart } from '@/utils/chartDetection';
import { format } from 'date-fns';

interface ChartRendererProps {
    chartType: ChartType;
    columns: string[];
    rows: any[][];
    rowCount: number;
    vizConfig?: any;
    height?: number | `${number}%`;
}

// Premium Color Palette
const COLORS = [
    '#6366f1', // Indigo
    '#8b5cf6', // Violet
    '#ec4899', // Pink
    '#10b981', // Emerald
    '#f59e0b', // Amber
    '#3b82f6', // Blue
    '#ef4444', // Red
    '#14b8a6', // Teal
];

// Helper to format large numbers (e.g. 1.2M, 4k)
const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toLocaleString();
};

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-background/95 backdrop-blur-md border border-border/50 p-3 rounded-xl shadow-xl text-xs min-w-[150px]">
                <p className="font-semibold mb-2 text-foreground/80 border-b border-border/50 pb-1">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} className="flex items-center justify-between gap-4 py-0.5">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-muted-foreground capitalize">{entry.name}</span>
                        </div>
                        <span className="font-mono font-medium text-foreground">
                            {typeof entry.value === 'number' ? formatNumber(entry.value) : entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

export const ChartRenderer: React.FC<ChartRendererProps> = ({
    chartType,
    columns,
    rows,
    rowCount,
    vizConfig,
    height = "100%",
}) => {
    if (chartType === 'table') {
        return null; // Table is rendered separately
    }

    const chartData = transformDataForChart(columns, rows);
    const { data, xKey, yKeys } = chartData;

    // Aggressive Performance Optimization
    // 1000 rendered DOM nodes (SVG Rects) is too slow for React/Recharts interaction.
    // We cap to 400 points. On a typical 1080p screen column, >400 bars is sub-pixel anyway.
    const DISPLAY_LIMIT = 400;
    const isPerformanceMode = data.length > 50;

    const displayData = React.useMemo(() => {
        return data.slice(0, DISPLAY_LIMIT);
    }, [data]);

    const CommonAxisProps = {
        tick: { fill: '#64748b', fontSize: 11 },
        axisLine: { stroke: '#e2e8f0', strokeWidth: 1 },
        tickLine: { stroke: '#e2e8f0' }
    };

    const renderGradients = () => {
        // Skip gradients in performance mode to save GPU/Rendering time
        if (isPerformanceMode) return null;

        return (
            <defs>
                {COLORS.map((color, index) => (
                    <linearGradient key={`colorUv${index}`} id={`colorUv${index}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                    </linearGradient>
                ))}
            </defs>
        );
    };

    // Helper to determine fill style
    const getFill = (index: number) => {
        if (isPerformanceMode) return COLORS[index % COLORS.length];
        return `url(#colorUv${index % COLORS.length})`;
    };

    const renderChart = () => {
        switch (chartType) {
            case 'bar':
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <BarChart data={displayData} margin={{ top: 5, right: 10, left: 5, bottom: 15 }}>
                            {renderGradients()}
                            {/* Remove Grid in performance mode to reduce DOM nodes */}
                            {!isPerformanceMode && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />}

                            <XAxis
                                dataKey={xKey}
                                {...CommonAxisProps}
                                minTickGap={30}
                                tickFormatter={(val) => val.toString().length > 10 ? `${val.toString().substring(0, 10)}...` : val}
                                dy={10}
                            />
                            <YAxis
                                {...CommonAxisProps}
                                tickFormatter={formatNumber}
                            />
                            {/* Simple Tooltip (no cursor highlight) for perf mode */}
                            <Tooltip
                                content={<CustomTooltip />}
                                cursor={isPerformanceMode ? false : { fill: '#f1f5f9', opacity: 0.5 }}
                                isAnimationActive={!isPerformanceMode}
                            />
                            <Legend wrapperStyle={{ paddingTop: '10px' }} iconType="circle" />

                            {yKeys.map((key, idx) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    fill={getFill(idx)}
                                    // Remove stroke in perf mode for speed
                                    stroke={isPerformanceMode ? "none" : COLORS[idx % COLORS.length]}
                                    // No rounded corners in perf mode
                                    radius={isPerformanceMode ? [0, 0, 0, 0] : [6, 6, 0, 0]}
                                    maxBarSize={60}
                                    isAnimationActive={!isPerformanceMode}
                                />
                            ))}
                            {/* Brush is very expensive. Only show if absolutely needed, and use simple stroke */}
                            {displayData.length > 20 && (
                                <Brush
                                    dataKey={xKey}
                                    height={30}
                                    stroke="#cbd5e1"
                                    travellerWidth={10}
                                    tickFormatter={() => ""}
                                    fill="#f8fafc"
                                    y={360}
                                />
                            )}
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'area':
            case 'line': // Treat line efficiently same style or area style
                const ChartComponent = chartType === 'area' ? AreaChart : LineChart;
                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <ChartComponent data={displayData} margin={{ top: 5, right: 10, left: 5, bottom: 15 }}>
                            {renderGradients()}
                            {!isPerformanceMode && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />}
                            <XAxis
                                dataKey={xKey}
                                {...CommonAxisProps}
                                minTickGap={30}
                                tickFormatter={(val) => val.toString().length > 10 ? `${val.toString().substring(0, 10)}...` : val}
                                dy={10}
                            />
                            <YAxis
                                {...CommonAxisProps}
                                tickFormatter={formatNumber}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={!isPerformanceMode} isAnimationActive={!isPerformanceMode} />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                            {yKeys.map((key, idx) => (
                                chartType === 'area' ? (
                                    <Area
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={COLORS[idx % COLORS.length]}
                                        fillOpacity={1}
                                        fill={getFill(idx)}
                                        strokeWidth={isPerformanceMode ? 1 : 2}
                                        isAnimationActive={!isPerformanceMode}
                                    />
                                ) : (
                                    <Line
                                        key={key}
                                        type="monotone"
                                        dataKey={key}
                                        stroke={COLORS[idx % COLORS.length]}
                                        strokeWidth={isPerformanceMode ? 1.5 : 3}
                                        dot={false}
                                        activeDot={!isPerformanceMode ? { r: 6, strokeWidth: 0 } : false}
                                        isAnimationActive={!isPerformanceMode}
                                    />
                                )
                            ))}
                            {displayData.length > 20 && (
                                <Brush
                                    dataKey={xKey}
                                    height={30}
                                    stroke="#cbd5e1"
                                    travellerWidth={10}
                                    tickFormatter={() => ""}
                                    fill="#f8fafc"
                                />
                            )}
                        </ChartComponent>
                    </ResponsiveContainer>
                );

            case 'pie':
                // For pie chart, use first numeric column
                const pieData = displayData.map((item) => ({
                    name: item[xKey],
                    value: parseFloat(item[yKeys[0]]) || 0,
                })).filter(d => d.value > 0).sort((a, b) => b.value - a.value).slice(0, 10); // Check top 10 for Pie

                return (
                    <ResponsiveContainer width="100%" height={height}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="value"
                                stroke="none"
                                isAnimationActive={!isPerformanceMode}
                            >
                                {pieData.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" layout="vertical" align="right" verticalAlign="middle" />
                        </PieChart>
                    </ResponsiveContainer>
                );

            default:
                return null;
        }
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center p-2">
            {renderChart()}
            {data.length > DISPLAY_LIMIT && (
                <p className="text-[10px] text-muted-foreground text-center mt-2 flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
                    Data capped to {DISPLAY_LIMIT} points for performance
                </p>
            )}
        </div>
    );
};

// Optimization: Memoize to prevent re-renders on dashboard when other widgets update
export default React.memo(ChartRenderer);
